import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../App';
import axios from 'axios';
import BottomNavigation from '../components/BottomNavigation';
import LanguageToggle from '../components/LanguageToggle';
import SavedSearchForm from '../components/SavedSearchForm';
import ResendManageLink from '../components/ResendManageLink';
import { Search, MapPin, Plus, ShoppingBag, X, LogOut, ChevronDown, Package, Eye, Navigation, ShieldAlert, Info, ArrowLeftRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Feb 2026 — Marketplace refactor. This page (previously "Classifieds") is
// registered at both /marketplace (new) and /classifieds (legacy alias). It
// serves three tabs backed by the same collection via the `listingType` field:
//   • sale   — items for sale (default, historical behaviour)
//   • wanted — someone looking to buy or receive an item
//   • free   — items being given away
// Lost & Found moved OUT of this page into the Community Safety Hub — see
// /lost-found and /community-safety.
const LISTING_TABS = [
  { key: 'sale',   labelKey: 'marketplace.tabSell',   fallback: 'Sell',   icon: ShoppingBag },
  { key: 'wanted', labelKey: 'marketplace.tabWanted', fallback: 'Wanted', icon: Search },
  { key: 'swap',   labelKey: 'marketplace.tabSwap',   fallback: 'Swap',   icon: ArrowLeftRight },
  { key: 'free',   labelKey: 'marketplace.tabFree',   fallback: 'Free',   icon: Package },
];

const Marketplace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { logout, user } = useAuth();
  const [activeTab, setActiveTab] = useState('sale'); // 'sale' | 'wanted' | 'free'
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [nearestMe, setNearestMe] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState('');

  // ?tab=wanted / ?tab=free deep-link support (also lets Profile "My Marketplace
  // Listings" and the Community Safety chooser jump directly to a specific tab).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab && LISTING_TABS.some(t => t.key === tab)) setActiveTab(tab);
  }, [location.search]);

  useEffect(() => {
    axios.get(`${API}/api/classifieds/categories`)
      .then(res => setCategories(res.data.categories))
      .catch(() => {});
  }, []);

  const fetchItems = useCallback(async (pg = 1) => {
    try {
      setLoading(true);
      const params = { page: pg, limit: 20, listingType: activeTab };
      if (searchTerm) params.search = searchTerm;
      if (selectedCategory) params.category = selectedCategory;
      if (locationFilter) params.location = locationFilter;
      if (nearestMe && userLocation) {
        params.lat = userLocation.lat;
        params.lng = userLocation.lng;
      }
      const res = await axios.get(`${API}/api/classifieds`, { params });
      setItems(res.data.items);
      setTotalPages(res.data.pages);
      setPage(res.data.page);
    } catch (err) {
      console.error('Error fetching marketplace:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchTerm, selectedCategory, locationFilter, nearestMe, userLocation]);

  useEffect(() => {
    fetchItems(1);
  }, [fetchItems]);

  const handleSearch = () => fetchItems(1);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setLocationFilter('');
    setNearestMe(false);
    setLocationError('');
  };

  const toggleNearestMe = () => {
    if (nearestMe) {
      setNearestMe(false);
      setLocationError('');
      return;
    }
    setLocationError('');
    if (userLocation) {
      setNearestMe(true);
      return;
    }
    if (!navigator.geolocation) {
      setLocationError(t('nearestMe.unavailable', 'Location is not available on this device'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setNearestMe(true);
      },
      () => {
        setLocationError(t('nearestMe.denied', 'Please enable location to use Nearest Me'));
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const hasFilters = searchTerm || selectedCategory || locationFilter || nearestMe;

  const formatPrice = (price) => {
    if (price === null || price === undefined) return t('classifieds.negotiable', 'Negotiable');
    return `${price.toFixed(2)}`;
  };

  const timeAgo = (dateStr) => {
    const now = new Date();
    const created = new Date(dateStr);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}m`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d`;
    return created.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const categoryIcons = {
    'Tools': '🔧', 'Furniture': '🪑', 'Cars & Vehicles': '🚗', 'Electronics': '📱',
    'Property': '🏠', 'Clothing & Fashion': '👗', 'Home & Garden': '🌿',
    'Sports & Leisure': '⚽', 'Baby & Kids': '👶', 'General': '📦'
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="marketplace-title">
              <ShoppingBag className="h-6 w-6" />
              {t('marketplace.title', 'Marketplace')}
            </h1>
            <div className="flex items-center gap-3">
              <LanguageToggle />
              {user ? (
                <button onClick={() => { logout(); navigate('/'); }}
                  className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                  data-testid="logout-button">
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('logout', 'Logout')}</span>
                </button>
              ) : (
                <button onClick={() => navigate('/signin')}
                  className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                  data-testid="signin-button">
                  <span>{t('signIn', 'Sign In')}</span>
                </button>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="flex gap-2">
            <input
              type="text"
              placeholder={t('marketplace.searchPlaceholder', 'Search the marketplace...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1 px-4 py-2.5 rounded-lg text-gray-900 focus:ring-2 focus:ring-white outline-none"
              data-testid="classifieds-search-input"
            />
            <button
              onClick={handleSearch}
              className="px-4 py-2.5 bg-white text-emerald-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              data-testid="classifieds-search-btn"
            >
              <Search className="h-5 w-5" />
            </button>
            <button
              onClick={toggleNearestMe}
              className={`px-4 py-2.5 rounded-lg font-semibold transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                nearestMe ? 'bg-white text-emerald-700' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              data-testid="nearest-me-btn"
            >
              <Navigation className={`h-4 w-4 ${nearestMe ? 'fill-current' : ''}`} />
              <span className="hidden sm:inline">{t('nearestMe.label', 'Nearest Me')}</span>
            </button>
          </div>
          {locationError && (
            <p className="mt-2 text-sm text-amber-200 bg-white/10 px-3 py-1.5 rounded-lg" data-testid="location-error">
              {locationError}
            </p>
          )}
        </div>
      </header>

      {/* Where-to-post hint card — Feb 2026: Marketplace vs Community Safety */}
      <div className="bg-purple-50 border-b border-purple-100">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <details className="group" data-testid="where-to-post-hint">
            <summary className="flex items-center gap-2 cursor-pointer list-none text-sm font-semibold text-purple-900 select-none">
              <Info className="h-4 w-4 text-purple-600 shrink-0" aria-hidden="true" />
              <span className="flex-1">{t('marketplace.hintTitle', 'Where should I post this?')}</span>
              <ChevronDown className="h-4 w-4 text-purple-600 transition-transform group-open:rotate-180" aria-hidden="true" />
            </summary>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="flex items-start gap-2 p-3 rounded-lg bg-white border border-emerald-200">
                <ShoppingBag className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-emerald-900">{t('marketplace.hintMarketTitle', 'Buying, selling, swapping or giving away')}</div>
                  <div className="text-xs text-emerald-700/80 mt-0.5">{t('marketplace.hintMarketDesc', 'You are in the right place — use the tabs below.')}</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/community-safety')}
                className="flex items-start gap-2 p-3 rounded-lg bg-white border border-amber-200 hover:border-amber-400 hover:bg-amber-50 transition-colors text-left"
                data-testid="hint-card-safety"
              >
                <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-amber-900">{t('marketplace.hintSafetyTitle', 'Lost & found, help needed or a safety concern')}</div>
                  <div className="text-xs text-amber-700/80 mt-0.5">{t('marketplace.hintSafetyDesc', 'Go to the Community Safety Hub instead.')}</div>
                </div>
              </button>
            </div>
          </details>
        </div>
      </div>

      {/* Marketplace tabs: Sell / Wanted / Free */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex" role="tablist" data-testid="marketplace-tabs">
            {LISTING_TABS.map(({ key, labelKey, fallback, icon: Icon }) => (
              <button
                key={key}
                onClick={() => { setActiveTab(key); setPage(1); }}
                className={`flex-1 py-3 text-[13px] sm:text-sm font-semibold text-center border-b-2 transition-colors flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 ${
                  activeTab === key
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`marketplace-tab-${key}`}
                role="tab"
                aria-selected={activeTab === key}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {t(labelKey, fallback)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* SWAP-2-DAY identity banner — only on the Swap tab */}
      {activeTab === 'swap' && (
        <div className="bg-gradient-to-r from-teal-50 to-emerald-50 border-b border-teal-100" data-testid="swap-2day-banner">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-2xl" aria-hidden="true">🔄</span>
              <div>
                <div className="text-base font-black tracking-tight text-teal-800">{t('marketplace.swapTitle', 'SWAP-2-DAY')}</div>
                <div className="text-xs text-teal-700/80">{t('marketplace.swapSubtitle', 'What have you got? What would you swap it for?')}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <>

      {/* Category pills */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <button
              onClick={() => setSelectedCategory('')}
              className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-colors ${
                selectedCategory === '' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="classified-cat-all"
            >
              {t('all', 'All')}
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  selectedCategory === cat ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid={`classified-cat-${cat.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
              >
                <span>{categoryIcons[cat] || '📦'}</span>
                {t(`classifieds.cat.${cat}`, cat)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Active filters bar */}
      {hasFilters && (
        <div className="bg-emerald-50 border-b border-emerald-100">
          <div className="max-w-7xl mx-auto px-4 py-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-emerald-700 font-medium">{t('activeFilters', 'Active Filters')}:</span>
              {nearestMe && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                  <Navigation className="h-3 w-3" /> {t('nearestMe.label', 'Nearest Me')}
                  <button onClick={() => { setNearestMe(false); setLocationError(''); }} data-testid="remove-nearest-filter"><X className="h-3 w-3" /></button>
                </span>
              )}
              {selectedCategory && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                  {selectedCategory}
                  <button onClick={() => setSelectedCategory('')} data-testid="remove-cat-filter"><X className="h-3 w-3" /></button>
                </span>
              )}
              {searchTerm && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                  &ldquo;{searchTerm}&rdquo;
                  <button onClick={() => setSearchTerm('')} data-testid="remove-search-filter"><X className="h-3 w-3" /></button>
                </span>
              )}
              <button
                onClick={clearFilters}
                className="ml-auto px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-full hover:bg-emerald-700 transition-colors"
                data-testid="classifieds-reset-filters"
              >
                {t('resetFilters', 'Reset Filters')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post button - floating.  Passes activeTab through so the composer
          creates the correct listingType. */}
      <button
        onClick={() => navigate(`/classifieds/post?listingType=${activeTab}`)}
        className="fixed bottom-24 right-4 z-40 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full p-4 shadow-lg transition-all hover:scale-105"
        data-testid="post-classified-fab"
        title={t('marketplace.postItem', 'Post an item')}
      >
        <Plus className="h-6 w-6" />
      </button>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-pulse">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1,2,3,4,5,6].map(i => (
                  <div key={i} className="bg-gray-200 h-48 rounded-xl"></div>
                ))}
              </div>
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-white rounded-xl shadow p-8">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg mb-2">{t('classifieds.noItems', 'No items found')}</p>
              <p className="text-gray-400 text-sm mb-6">
                {hasFilters
                  ? t('classifieds.tryDifferent', 'Try adjusting your search or filters')
                  : t('classifieds.beFirst', 'Be the first to post something for sale!')}
              </p>
              {hasFilters ? (
                <button onClick={clearFilters}
                  className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                  data-testid="classifieds-clear-btn">
                  {t('clearFilters', 'Clear Filters')}
                </button>
              ) : (
                <button onClick={() => navigate(`/classifieds/post?listingType=${activeTab}`)}
                  className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                  data-testid="classifieds-post-first-btn">
                  {t('marketplace.postItem', 'Post an item')}
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <ResendManageLink />
              <SavedSearchForm categories={categories} currentSearch={searchTerm} currentCategory={selectedCategory} />
            </div>

            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">
                {items.length} {items.length === 1 ? t('classifieds.itemFound', 'item') : t('classifieds.itemsFound', 'items')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  onClick={() => navigate(`/classifieds/${item.id}`)}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer overflow-hidden group border border-gray-100"
                  data-testid={`classified-card-${item.id}`}
                >
                  {/* Image or placeholder */}
                  <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-emerald-50 to-teal-50">
                    {item.photos && item.photos.length > 0 ? (
                      <img
                        src={item.photos[0]}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        style={{ objectPosition: 'center center' }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-4xl">{categoryIcons[item.category] || '📦'}</span>
                      </div>
                    )}
                    {/* Status badge */}
                    {item.status === 'pending_collection' && (
                      <div className="absolute top-2 left-2">
                        <span className="bg-amber-400 text-amber-900 px-2.5 py-1 rounded-full text-xs font-bold uppercase">
                          {t('classifieds.statusPending', 'Pending Collection')}
                        </span>
                      </div>
                    )}
                    {/* Photo count */}
                    {item.photos && item.photos.length > 1 && (
                      <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                        {item.photos.length} photos
                      </span>
                    )}
                  </div>

                  <div className="p-3.5">
                    {/* Price */}
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-lg font-bold text-gray-900" data-testid={`classified-price-${item.id}`}>
                        {item.listingType === 'swap'
                          ? <span className="inline-flex items-center gap-1 text-teal-700"><ArrowLeftRight className="h-4 w-4" />{t('marketplace.swapLabel', 'Swap')}</span>
                          : (item.price !== null && item.price !== undefined
                              ? <>€{formatPrice(item.price)}{item.listingType === 'sell_swap' && <span className="text-xs font-bold text-teal-700 ml-1">{t('marketplace.orSwap', 'OR SWAP')}</span>}</>
                              : t('classifieds.negotiable', 'Negotiable'))}
                      </p>
                      <span className="text-xs text-gray-400 whitespace-nowrap mt-1">{timeAgo(item.createdAt)}</span>
                    </div>

                    {/* Title */}
                    <h3 className="text-sm font-medium text-gray-800 line-clamp-2 mb-2" data-testid={`classified-title-${item.id}`}>
                      {item.title}
                    </h3>

                    {/* Category + Location + Distance */}
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                        {categoryIcons[item.category] || '📦'} {t(`classifieds.cat.${item.category}`, item.category)}
                      </span>
                      {item.distance != null && (
                        <span className="inline-flex items-center gap-0.5 text-emerald-600 font-medium" data-testid={`classified-distance-${item.id}`}>
                          <Navigation className="h-3 w-3" />
                          {item.distance} km
                        </span>
                      )}
                      {item.location && !item.distance && (
                        <span className="inline-flex items-center gap-0.5 truncate">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          {item.location}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (
                  <button
                    key={pg}
                    onClick={() => fetchItems(pg)}
                    className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                      pg === page ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border'
                    }`}
                    data-testid={`classifieds-page-${pg}`}
                  >
                    {pg}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <BottomNavigation />
      </>
    </div>
  );
};

export default Marketplace;
